import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';

export interface Conversation {
  id: string;
  tenant_id: string;
  business_account_id: string;
  contact_id: string;
  status: 'active' | 'archived' | 'blocked';
  last_message_at: string | null;
  last_inbound_message_at?: string | null;
  agent_stepped_in_at?: string | null;
  assigned_agent_id?: string | null;
  created_at: string;
  updated_at: string;
  contact?: {
    id: string;
    name: string | null;
    phone_number: string;
    profile_picture_url: string | null;
    status: string;
    last_seen: string | null;
  };
  last_message?: {
    id: string;
    content_text: string | null;
    message_type: string;
    is_inbound: boolean;
    created_at: string;
    status: string;
  };
  unread_count?: number;
  scheduled_message_count?: number;
}

export interface ConversationFilters {
  business_account_id?: string;
  status?: string;
  contact_id?: string;
  search?: string;
  priority?: 'high' | 'medium' | 'low';
  isRequested?: boolean;
  isSteppedIn?: boolean;
  limit?: number;
  offset?: number;
  pageToken?: string;
  orderBy?: string;
  ascending?: boolean;
  startDate?: string;
  endDate?: string;
  hasScheduledMessages?: boolean;
  isUnread?: boolean;
  assigned_to?: string;
  unassigned?: boolean;
  campaignId?: string;
  tagId?: string;
}

export interface AssignedAgentOption {
  id: string;
  name: string | null;
}

export interface CreateConversationData {
  contactId: string;
  businessAccountId: string;
  status?: string;
}

export const conversationsApi = {
  async getAll(filters: ConversationFilters = {}) {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const endpoint = `${API_ENDPOINTS.CONVERSATIONS}${params.toString() ? `?${params.toString()}` : ''}`;
    return apiClient.get<Conversation[]>(endpoint);
  },

  async getById(id: string) {
    return apiClient.get<Conversation>(`${API_ENDPOINTS.CONVERSATIONS}/${id}`);
  },

  async getByBusinessAccount(businessAccountId: string, filters: Omit<ConversationFilters, 'business_account_id'> = {}) {
    return this.getAll({ ...filters, business_account_id: businessAccountId });
  },

  async getRecent(limit: number = 5, businessAccountId?: string) {
    return this.getAll({
      business_account_id: businessAccountId,
      limit,
      orderBy: 'last_message_at',
      ascending: false,
    });
  },

  async getAssignedAgents(businessAccountId: string) {
    return apiClient.get<AssignedAgentOption[]>(
      `${API_ENDPOINTS.CONVERSATIONS}/assigned-agents?businessAccountId=${encodeURIComponent(businessAccountId)}`
    );
  },

  async getStarred(businessAccountId?: string, limit = 200) {
    const qs = new URLSearchParams();
    if (businessAccountId) qs.append('business_account_id', businessAccountId);
    qs.append('limit', String(limit));
    return apiClient.get(`${API_ENDPOINTS.CONVERSATIONS}/starred?${qs.toString()}`);
  },

  async getUnreadSummary(_businessAccountId?: string | null) {
    return apiClient.get<{
      unreadConversationCount: number;
      unreadMessageCount: number;
      requestedCount: number;
      steppedInCount: number;
      byConversation?: Record<string, number>;
      platformByConversation?: Record<string, string>;
      byConversationTruncated?: boolean;
    }>(`${API_ENDPOINTS.CONVERSATIONS}/unread-summary`);
  },

  async create(data: CreateConversationData) {
    return apiClient.post<Conversation>(API_ENDPOINTS.CONVERSATIONS, data);
  },

  async updateStatus(id: string, status: Conversation['status']) {
    return apiClient.put<Conversation>(`${API_ENDPOINTS.CONVERSATIONS}/${id}`, { status });
  },

  async archive(id: string) {
    return this.updateStatus(id, 'archived');
  },

  async unarchive(id: string) {
    return this.updateStatus(id, 'active');
  },

  async delete(id: string) {
    return apiClient.delete<{ success: boolean; message: string }>(`${API_ENDPOINTS.CONVERSATIONS}/${id}`);
  },

  async stepIn(id: string) {
    return apiClient.post<{ success: boolean; data: { id: string; agentSteppedInAt: string } }>(
      `${API_ENDPOINTS.CONVERSATIONS}/${id}/step-in`
    );
  },

  async assign(id: string, agentId: string | null | '') {
    return apiClient.post<{ success: boolean; data: { id: string; assignedAgentId: string | null } }>(
      `${API_ENDPOINTS.CONVERSATIONS}/${id}/assign`,
      { agentId: agentId || null }
    );
  },

  async updatePriority(id: string, priority: 'high' | 'medium' | 'low' | null) {
    return apiClient.patch<{ success: boolean; data: { id: string; priority: string | null } }>(
      `${API_ENDPOINTS.CONVERSATIONS}/${id}/priority`,
      { priority }
    );
  },

  async getTabUnreadCounts(businessAccountId: string) {
    return apiClient.get<{
      requestedUnreadTotal: number;
      steppedInUnreadTotal: number;
    }>(`${API_ENDPOINTS.CONVERSATIONS}/stats/tab-counts`, {
      params: {
        business_account_id: businessAccountId,
      },
    });
  },

  async getHighPriorityCount(businessAccountId: string) {
    return apiClient.get<{
      success: boolean;
      data: {
        highPriorityCount: number;
      };
    }>(`${API_ENDPOINTS.CONVERSATIONS}/stats/high-priority`, {
      params: {
        business_account_id: businessAccountId,
      },
    });
  },
};
